import { gql } from '@apollo/client';
import { useContext } from 'react';
import type { Language } from '@apiTypes';
import { ProductQuerySort } from '@apiTypes';
import useCancelableQuery from '../../core/hooks/apollo/useCancelableQuery';
import useStoreAndLanguageParams from '../../core/hooks/useStoreAndLanguageParams';
import PreorderModeContext from '../../preorder/PreorderModeContext';
import type { SearchResultProduct } from './useProductSearch';

const productListQuery = gql`
    query SearchProductsList(
        $filters: ProductQueryFilters!
        $pagination: Pagination!
        $context: ProductQueryContext!
        $sort: ProductQuerySort!
    ) {
        productsList(
            filters: $filters
            pagination: $pagination
            context: $context
            sort: $sort
        ) {
            items {
                id
                key
                name
                isNew: new
                onSale
                onSale
                manufacturer
                sku
                summary
                media {
                    type
                    url
                    renditionUrl
                    mediaUrl
                }
                tileAttributes {
                    name
                    value
                }
                categories {
                    id
                    name
                }
            }
            total
        }
    }
`;

const useNewProductSearch = ({
    search,
    skip,
    pageSize = 12,
}: {
    search: string;
    skip?: boolean;
    pageSize?: number;
}) => {
    const { store, language } = useStoreAndLanguageParams();
    const { productsFilters } = useContext(PreorderModeContext);

    const { loading, data, error } = useCancelableQuery<{
        productsList?: {
            items: SearchResultProduct[];
            total: number;
        };
    }>(
        productListQuery,
        {
            variables: {
                filters: {
                    search,
                },
                context: {
                    storeCode: store,
                    language: language as Language,
                    preorderSeasonId: productsFilters.preorderSeasonId,
                },
                pagination: {
                    size: pageSize,
                    from: 0,
                },
                sort: ProductQuerySort.Relevance,
            },
            skip,
            fetchPolicy: 'cache-first',
        },
        true
    );

    const items = data?.productsList?.items || [];
    const total = data?.productsList?.total;

    return {
        loading,
        products: items,
        total,
        error,
    };
};

export default useNewProductSearch;
